import type { TransformedVersionField } from '@atlassian/jira-business-fields/src/common/field-types/version/types.tsx';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import type { Version } from '@atlassian/jira-issue-shared-types/src/common/types/issue-type.tsx';
import {
	MULTI_VERSION_CF_TYPE,
	VERSION_CF_TYPE,
	AFFECTS_VERSIONS_TYPE,
	FIX_VERSIONS_TYPE,
} from '@atlassian/jira-platform-field-config';
import { createClipboardHelperComponent } from '../../../clipboard-handler/create-clipboard-helper-component';
import { useClipboardHandler } from '../../../clipboard-handler/use-clipboard-handler';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasPlainTextFormat,
} from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	PasteHandler,
	PasteMetadata,
	ListIssueForCopyPaste,
} from '../../../types';
import { versionSupportedTypes } from '../supported-field-types';

const isTransformedSingleVersion = (option: unknown): option is Version =>
	!!option && typeof option === 'object' && 'id' in option && 'name' in option;

const isTransformedMultipleVersions = (fieldValue: unknown): fieldValue is Version[] =>
	Array.isArray(fieldValue) && fieldValue.every(isTransformedSingleVersion);

const emptyValue: [] = [];

function assertVersionField(
	field: ListIssueFieldTransformed,
): asserts field is TransformedVersionField {
	if (!field) {
		throw new Error('Field is required');
	}

	if (
		field.type !== MULTI_VERSION_CF_TYPE &&
		field.type !== AFFECTS_VERSIONS_TYPE &&
		field.type !== FIX_VERSIONS_TYPE &&
		field.type !== VERSION_CF_TYPE
	) {
		throw new Error('Field type is not supported');
	}
}

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): Version | Version[] | null => {
	if (
		!assertClipboardHasPlainTextFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, versionSupportedTypes)
	) {
		return null;
	}

	if (
		!isTransformedMultipleVersions(metadata?.fieldValue) &&
		!isTransformedSingleVersion(metadata?.fieldValue)
	) {
		return null;
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	if (!(versionSupportedTypes as string[]).includes(metadata.fieldType)) {
		return null;
	}

	return metadata?.fieldValue;
};

const getInitialValue = (field: ListIssueFieldTransformed) => {
	if (field.type === VERSION_CF_TYPE) {
		return field.value ?? null;
	}
	return isTransformedMultipleVersions(field.value) ? field.value : emptyValue;
};

const useVersionClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	assertVersionField(field);

	const [{ value }, { saveValue }] = useEditField<Version | Version[] | null, null>({
		fieldKey: field.fieldId,
		issueKey: issue.key,
		fieldType: field.type,
		initialValue: getInitialValue(field),
	});

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: isTransformedSingleVersion(field.value)
			? field.value?.name ?? null
			: field.value?.map((v) => v.name).join(',') ?? null,
		currentValue: value,
		getPastedValue,
		saveValue,
	});
};

const VersionClipboardHelper = createClipboardHelperComponent(useVersionClipboardHandler);

export const versionClipboardHandler = {
	[VERSION_CF_TYPE]: VersionClipboardHelper,
	[MULTI_VERSION_CF_TYPE]: VersionClipboardHelper,
	[AFFECTS_VERSIONS_TYPE]: VersionClipboardHelper,
	[FIX_VERSIONS_TYPE]: VersionClipboardHelper,
};
